import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, BookOpen, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { blogArticles } from "@/data/blogArticles";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";

const ARTICLES_PER_PAGE = 6;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  const categories = [...new Set(blogArticles.map(a => a.category))];

  const filteredArticles = useMemo(() => {
    return blogArticles.filter(article => {
      const matchesSearch = searchQuery === "" || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = !selectedCategory || article.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Reset to page 1 when filters change
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  const handleFilterChange = (newSearch: string, newCategory: string | null) => {
    setSearchQuery(newSearch);
    setSelectedCategory(newCategory);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <SEO 
        title="Blog - Conseils Gestion Documentaire"
        description="Découvrez nos articles sur la gestion documentaire, l'organisation de fichiers, la sécurité des données et l'utilisation de l'IA pour vos documents."
        canonical="/blog"
        ogType="website"
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-border/50 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={easyfinderLogo} alt="EasyFinder" className="w-9 h-9 rounded-xl shadow-lg shadow-primary/25" />
            <span className="text-lg font-semibold tracking-tight">EasyFinder</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Accueil
            </Link>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Tarifs
            </Link>
            <Link to="/blog" className="text-sm font-medium text-foreground">
              Blog
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm">Connexion</Button>
            </Link>
            <Link to="/auth?mode=signup">
              <Button size="sm" variant="glow">Commencer</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div {...fadeInUp}>
            <Badge variant="secondary" className="mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              Blog EasyFinder
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Conseils & Actualités
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Découvrez nos articles sur la gestion documentaire, l'organisation de fichiers, 
              la sécurité des données et l'utilisation de l'IA pour optimiser votre productivité.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-md mx-auto mt-8">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => handleFilterChange(e.target.value, selectedCategory)}
                className="pl-10 pr-10 h-11 bg-background/80 backdrop-blur-sm border-border/50"
              />
              {searchQuery && (
                <button
                  onClick={() => handleFilterChange("", selectedCategory)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 pb-8">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-wrap justify-center gap-2">
            <Badge 
              variant={selectedCategory === null ? "default" : "outline"}
              className="px-4 py-1.5 cursor-pointer hover:bg-primary/10 transition-colors"
              onClick={() => handleFilterChange(searchQuery, null)}
            >
              Tous
            </Badge>
            {categories.map((category) => (
              <Badge 
                key={category} 
                variant={selectedCategory === category ? "default" : "outline"}
                className="px-4 py-1.5 cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => handleFilterChange(searchQuery, category === selectedCategory ? null : category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="px-6 pb-24">
        <div className="container mx-auto max-w-5xl">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">Aucun article trouvé</h3>
              <p className="text-muted-foreground">
                Essayez avec d'autres mots-clés ou explorez nos catégories.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => handleFilterChange("", null)}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
          <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/blog/${article.slug}`}>
                  <Card className="h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group">
                    {/* Article Image */}
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    
                    <CardContent className="p-5">
                      <Badge variant="secondary" className="mb-3 text-xs">
                        {article.category}
                      </Badge>
                      
                      <h2 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {article.title}
                      </h2>
                      
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {article.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(article.publishedAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {article.readTime}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-9"
                >
                  {page}
                </Button>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-24">
        <div className="container mx-auto max-w-3xl">
          <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">
                Prêt à organiser vos documents ?
              </h2>
              <p className="text-muted-foreground mb-6">
                Essayez EasyFinder gratuitement et découvrez la recherche documentaire intelligente.
              </p>
              <Link to="/auth?mode=signup">
                <Button variant="glow" size="lg">
                  Commencer gratuitement
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8 px-6">
        <div className="container mx-auto max-w-5xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={easyfinderLogo} alt="EasyFinder" className="w-6 h-6 rounded-lg" />
            <span className="text-sm text-muted-foreground">© 2024 EasyFinder</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">
              Accueil
            </Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Confidentialité
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              CGU
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
